/**
 * The header's alarm-timeline drawer — a button on EVERY page (`Shell.tsx`'s own header,
 * beside full screen and the theme toggle) that opens a right-side panel of recent alarm
 * activity as a vertical timeline, live off the same store the bell already reads
 * (`app/alarm-feed.ts`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS BESIDE THE BELL, NOT INSTEAD OF IT
 * ─────────────────────────────────────────────────────────────────────────────
 * `AlarmBell` is a glance — a badge and a short popover, built to answer "has anything
 * happened since I looked". This is a WORKSPACE: wider, one row per event with the elapsed
 * time between them, and every row opens that alarm's own page. An operator deep in
 * Telemetry or the Twin who hears about a new fault should not have to leave the screen they
 * are reading to go open Alarms — the drawer overlays instead (`Drawer.tsx`'s whole reason:
 * a Highcharts/Mapbox/SLD screen underneath must not resize when this opens).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * "ACKNOWLEDGED" IS AS FINE-GRAINED AS THE STREAM GETS
 * ─────────────────────────────────────────────────────────────────────────────
 * `alarm_notify()` (migration 0026) emits one of `opened / reopened / closed / changed` —
 * every lifecycle move other than open/close (acknowledge, assign, note, verify, block…)
 * collapses into `changed`. This timeline reads that honestly ("updated") rather than
 * guessing which kind of change it was; a caller wanting the exact transition already has the
 * alarm's own page, which this drawer's rows open in one click.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import type { AlarmListResponse } from '@/lib/api/types';
import { useSiteContext } from '@/app/site-context-value';
import { useAlarmFeed } from '@/app/alarm-feed';
import type { LiveAlarm } from '@/lib/api/events';
import { Drawer } from './Drawer';
import { Icon } from './Icon';
import { isRuleIdentity, liveAlarmLabel, readableAlarmName } from './alarm-readable';
import { useRuleNameOf } from '@/lib/api/rules';

const HISTORY_MS = 24 * 3_600_000;

interface Entry {
  key: string;
  id: string;
  event: LiveAlarm['event'];
  at: number;
  severity: string;
  label: string;
  device: string;
  site: string;
}

const VERB: Record<LiveAlarm['event'], string> = {
  opened: 'raised',
  reopened: 're-raised',
  closed: 'cleared',
  changed: 'updated',
};

/**
 * HISTORY FIRST, LIVE ON TOP (QA pass, 2026-09-29). The drawer used to show only events pushed
 * since the page loaded, so at night — or on any fresh load — it opened on a paragraph saying
 * it was empty. It now loads the last 24 h of alarm rows when opened (one `/alarms` read, the
 * same endpoint the board uses), turns each into its raised/cleared events, and merges the live
 * stream over them, de-duplicated. History rows carry the SERVER-resolved label (`name`, see
 * `modules/alarms/service.ts`), which also answers most of the raw-identity gap without
 * pulling `alarm-label.ts` into the eager bundle.
 *
 * Live events carry the trigger's raw identity; `alarm-readable.ts` names them, shared with
 * the toasts and the bell so one alarm reads the same in all three.
 */
function tone(a: Pick<LiveAlarm, 'event' | 'severity'>): string {
  // Cleared is not a verdict — green means pass, and a cleared alarm is simply over.
  if (a.event === 'closed') return 'var(--muted-fg)';
  const s = a.severity.toLowerCase();
  if (s.includes('critical')) return 'var(--danger)';
  if (s.includes('major') || s.includes('warn')) return 'var(--warn)';
  return 'var(--info)';
}

function elapsed(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86_400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86_400)}d`;
}

/** The one control this whole file exists to add — self-contained, so `Shell.tsx` renders it
 *  the same way it renders the theme toggle, with no state to thread through App.tsx. */
export function AlarmActivityButton({ onOpenAlarm }: { onOpenAlarm: (id: string) => void }): ReactNode {
  const [open, setOpen] = useState(false);
  const recent = useAlarmFeed((s) => s.recent);
  const stream = useAlarmFeed((s) => s.stream);
  const { selectedId } = useSiteContext();
  const history = useQuery({
    queryKey: ['alarm-drawer-history', selectedId],
    queryFn: () =>
      api.get<AlarmListResponse>('/alarms', {
        ...(selectedId ? { siteId: selectedId } : {}),
        state: 'all',
        from: new Date(Date.now() - HISTORY_MS).toISOString(),
        limit: 100,
      }),
    enabled: open,
    staleTime: 30_000,
  });

  const ruleName = useRuleNameOf(open && recent.some((a) => isRuleIdentity(a.name)));

  const entries = useMemo((): Entry[] => {
    const cutoff = Date.now() - HISTORY_MS;
    const out = new Map<string, Entry>();
    for (const r of history.data?.data ?? []) {
      const label = r.source === 'silence' ? (r.deviceId === null ? 'No data received (platform)' : 'Communication lost') : readableAlarmName(r.name ?? 'Alarm');
      const base = { id: r.id, severity: r.severity, label, device: r.deviceName, site: r.siteName };
      const opened = Date.parse(r.openedAt);
      if (opened >= cutoff) out.set(`${r.id}-opened`, { ...base, key: `${r.id}-opened`, event: 'opened', at: opened });
      if (r.closedAt) {
        const closed = Date.parse(r.closedAt);
        if (closed >= cutoff) out.set(`${r.id}-closed`, { ...base, key: `${r.id}-closed`, event: 'closed', at: closed });
      }
    }
    for (const a of recent) {
      const key = `${a.id}-${a.event}`;
      // A live event for an alarm history already holds keeps the history's (resolved) label.
      const known = out.get(`${a.id}-opened`) ?? out.get(`${a.id}-closed`);
      out.set(key, {
        key,
        id: a.id,
        event: a.event,
        at: a.receivedAt,
        severity: a.severity,
        label: known?.label ?? liveAlarmLabel(a, ruleName),
        device: a.device,
        site: a.site,
      });
    }
    return [...out.values()].sort((x, y) => y.at - x.at).slice(0, 80);
  }, [history.data, recent, ruleName]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="lm-hov flex items-center justify-center rounded-[var(--radius-sm)]"
        style={{ width: 'var(--header-ctl-h)', height: 'var(--header-ctl-h)', color: 'var(--muted-fg)' }}
        aria-label="Alarm timeline"
        title="Recent alarm activity, across the whole session — opens a panel"
      >
        <Icon name="ic-activity" size={16} />
      </button>

      {open && (
        <Drawer title="Alarm activity" desc={stream === 'live' ? 'Last 24 h · live' : 'Last 24 h · not live'} onClose={() => setOpen(false)} width={420}>
          {history.isLoading ? (
            <div className="lm-sk h-[120px] rounded-[var(--radius-sm)]" />
          ) : entries.length === 0 ? (
            <p className="lm-t-base text-[var(--muted-fg)]">Nothing in the last 24 h.</p>
          ) : (
            <ol className="flex flex-col gap-0 border-l border-[var(--border)] pl-[14px]">
              {entries.map((a, i) => {
                const prev = entries[i - 1];
                const gapMs = prev ? prev.at - a.at : null;
                return (
                  <li key={a.key} className="relative pb-[14px]">
                    {gapMs !== null && gapMs > 0 && (
                      <div className="lm-t-micro -ml-[18px] mb-[4px] text-[var(--muted-fg)]">
                        +{elapsed(gapMs)} earlier
                      </div>
                    )}
                    <span
                      aria-hidden
                      className="absolute -left-[18px] top-[5px] size-[8px] rounded-full"
                      style={{ background: tone(a) }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        onOpenAlarm(a.id);
                      }}
                      className="lm-hov flex w-full flex-col rounded-[var(--radius-sm)] p-[6px_8px] text-left"
                    >
                      <span className="flex items-baseline gap-[6px]">
                        <span className="lm-t-sm font-bold" style={{ color: tone(a) }}>
                          {a.severity} {VERB[a.event]}
                        </span>
                        <span className="lm-t-micro lm-num text-[var(--muted-fg)]">
                          {elapsed(Date.now() - a.at)} ago
                        </span>
                      </span>
                      <span className="lm-t-base truncate text-[var(--fg)]">{a.label}</span>
                      <span className="lm-t-sm truncate text-[var(--muted-fg)]">{a.device} · {a.site}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </Drawer>
      )}
    </>
  );
}
